import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../services/api';
import Toast from './Toast';
import useToast from '../hooks/useToast';

/** Quick presets for Daily objective (hours:minutes) - same UX as Standard Time in Operations */
const DAILY_OBJECTIVE_PRESETS = [
  { labelKey: 'centres.preset4H', hours: 4, minutes: 0 },
  { labelKey: 'centres.preset6H', hours: 6, minutes: 0 },
  { labelKey: 'centres.preset7H', hours: 7, minutes: 0 },
  { labelKey: 'centres.preset8H', hours: 8, minutes: 0 },
];

/**
 * Daily objective spinner: same UI as Standard Time in operation add form.
 * Two fields (Hours 0–24, Minutes 0–59) with vertical +/- buttons; when hours is 24, minutes are 0.
 */
function DailyObjectiveSpinner({ hours, minutes, onChange, hasError, ariaErrorId, ariaHintId }) {
  const { t } = useTranslation(['admin', 'common']);
  const h = Math.max(0, Math.min(24, parseInt(hours, 10) || 0));
  const m = h === 24 ? 0 : Math.max(0, Math.min(59, parseInt(minutes, 10) || 0));
  const update = (newHours, newMinutes) => {
    const nh = Math.max(0, Math.min(24, newHours));
    const nm = nh === 24 ? 0 : Math.max(0, Math.min(59, newMinutes));
    onChange(nh, nm);
  };
  const incHours = () => update(h + 1, m);
  const decHours = () => update(h - 1, m);
  const incMinutes = () => update(h, m >= 59 ? 0 : m + 1);
  const decMinutes = () => update(h, m <= 0 ? 59 : m - 1);
  const handleHoursInput = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') { update(0, m); return; }
    update(Math.min(24, parseInt(raw, 10) || 0), m);
  };
  const handleMinutesInput = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') { update(h, 0); return; }
    update(h, Math.min(59, parseInt(raw, 10) || 0));
  };
  const inputClass = `flex-1 min-w-0 h-full text-center text-sm font-mono font-semibold bg-transparent text-slate-900 outline-none selection:bg-primary/20 border-0 rounded-none ${
    hasError ? 'text-red-700' : ''
  }`;
  const wrapperClass = `flex flex-1 items-stretch h-12 min-w-0 rounded-lg border overflow-hidden shadow-sm transition-all focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary ${
    hasError ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-slate-50'
  }`;
  const btnClass = 'flex items-center justify-center w-8 shrink-0 text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 transition-colors border-slate-200';
  return (
    <div className="flex w-full items-center gap-1.5">
      <div className={wrapperClass}>
        <input
          type="text"
          inputMode="numeric"
          value={String(h).padStart(2, '0')}
          onChange={handleHoursInput}
          onBlur={() => update(h, m)}
          maxLength={2}
          aria-label={t('common:hours')}
          className={inputClass}
          aria-describedby={[ariaHintId, ariaErrorId].filter(Boolean).join(' ') || undefined}
        />
        <div className="flex flex-col border-l border-slate-200">
          <button type="button" onClick={incHours} aria-label={t('centres.increaseHours')} className={`${btnClass} border-b border-slate-200`}>
            <span className="material-symbols-outlined text-[18px]">expand_less</span>
          </button>
          <button type="button" onClick={decHours} aria-label={t('centres.decreaseHours')} className={btnClass}>
            <span className="material-symbols-outlined text-[18px]">expand_more</span>
          </button>
        </div>
      </div>
      <span className="text-lg font-mono font-bold text-slate-400 select-none">:</span>
      <div className={wrapperClass}>
        <input
          type="text"
          inputMode="numeric"
          value={String(m).padStart(2, '0')}
          onChange={handleMinutesInput}
          onBlur={() => update(h, m)}
          maxLength={2}
          aria-label={t('centres.minutes')}
          className={inputClass}
          disabled={h === 24}
          aria-describedby={[ariaHintId, ariaErrorId].filter(Boolean).join(' ') || undefined}
        />
        <div className="flex flex-col border-l border-slate-200">
          <button type="button" onClick={incMinutes} aria-label={t('centres.increaseMinutes')} disabled={h === 24} className={`${btnClass} border-b border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent`}>
            <span className="material-symbols-outlined text-[18px]">expand_less</span>
          </button>
          <button type="button" onClick={decMinutes} aria-label={t('centres.decreaseMinutes')} disabled={h === 24} className={`${btnClass} disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent`}>
            <span className="material-symbols-outlined text-[18px]">expand_more</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const CentresPage = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [selectedCentre, setSelectedCentre] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [centres, setCentres] = useState([]);
  const [centresCount, setCentresCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loadingCentres, setLoadingCentres] = useState(false);
  const [centresError, setCentresError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // State for add centre modal
  const [showAddCentreModal, setShowAddCentreModal] = useState(false);
  const [addFormData, setAddFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    daily_objective_hours: 7,
    daily_objective_minutes: 0
  });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  // Form state for editing centre
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    daily_objective_hours: 7,
    daily_objective_minutes: 0
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, centreId: null, centreName: null, employeeCount: 0 });
  const [deleteTargetCentreId, setDeleteTargetCentreId] = useState('');
  const [otherCentresForDelete, setOtherCentresForDelete] = useState([]);
  const [deleteError, setDeleteError] = useState(null);
  const { toast, showSuccess, showError, showWarning, hideToast } = useToast();

  // Refs for status buttons animation
  const activeStatusRef = useRef(null);
  const inactiveStatusRef = useRef(null);
  const statusContainerRef = useRef(null);

  // Map API centre data to display format
  const mapCentreToDisplay = (centre) => {
    const h = centre.daily_objective_hours != null ? Number(centre.daily_objective_hours) : 7;
    const m = centre.daily_objective_minutes != null ? Number(centre.daily_objective_minutes) : 0;
    return {
      id: centre.id,
      name: centre.name || t('centres.unnamedCentre'),
      description: centre.description || '',
      employeeCount: centre.employee_count ?? centre.team_count ?? 0,
      dailyObjectiveHours: h,
      dailyObjectiveMinutes: m,
      status: centre.is_active ? t('common:active') : t('common:inactive'),
      statusColor: centre.is_active ? 'emerald' : 'red',
      createdDate: centre.created_at ? new Date(centre.created_at).toLocaleDateString() : t('common:na'),
      rawData: centre
    };
  };

  const selectedCentreData = centres.find(c => c.id === selectedCentre);

  // Initialize form data when centre is selected
  useEffect(() => {
    if (selectedCentreData?.rawData) {
      const centreData = selectedCentreData.rawData;
      setEditFormData({
        name: centreData.name || '',
        description: centreData.description || '',
        is_active: centreData.is_active !== undefined ? centreData.is_active : true,
        daily_objective_hours: centreData.daily_objective_hours != null ? Number(centreData.daily_objective_hours) : 7,
        daily_objective_minutes: centreData.daily_objective_minutes != null ? Number(centreData.daily_objective_minutes) : 0
      });
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [selectedCentreData]);

  // Fetch centres from backend
  const fetchCentres = async () => {
    setLoadingCentres(true);
    setCentresError(null);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (searchQuery) filters.search = searchQuery;

      const response = await api.getCentres(filters, currentPage, pageSize);
      
      // Handle paginated response structure
      const centresArray = Array.isArray(response) 
        ? response 
        : (response && response.results && Array.isArray(response.results)) 
          ? response.results 
          : [];
      
      const mappedCentres = centresArray.map(mapCentreToDisplay);
      setCentres(mappedCentres);
      
      // Update pagination metadata
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        setCentresCount(response.count || 0);
      } else {
        setCentresCount(mappedCentres.length);
      }
      
      // Clear selection if selected centre is not in current results
      if (selectedCentre && !mappedCentres.find(c => c.id === selectedCentre)) {
        setSelectedCentre(null);
      }
    } catch (error) {
      const formattedError = formatApiError(error);
      setCentresError(formattedError.generalError || t('centres.loadFailed'));
      console.error('Error fetching centres:', error);
    } finally {
      setLoadingCentres(false);
    }
  };

  // Reset to page 1 when filters change (except page itself)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchQuery]);

  // Fetch centres when filters, page, or page size changes
  useEffect(() => {
    fetchCentres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, statusFilter, searchQuery]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedCentre) {
          setSelectedCentre(null);
        }
        if (showAddCentreModal) {
          setShowAddCentreModal(false);
        }
      }
    };

    if (selectedCentre || showAddCentreModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedCentre, showAddCentreModal]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setAddError(null);
    setAddSuccess(false);
  };

  const handleOpenAddModal = () => {
    setAddFormData({
      name: '',
      description: '',
      is_active: true,
      daily_objective_hours: 7,
      daily_objective_minutes: 0
    });
    setAddError(null);
    setAddSuccess(false);
    setShowAddCentreModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddCentreModal(false);
    setAddError(null);
    setAddSuccess(false);
  };

  const handleAddCentre = async () => {
    // Validate required fields
    if (!addFormData.name || addFormData.name.trim() === '') {
      setAddError(t('centres.nameRequired'));
      return;
    }

    setAdding(true);
    setAddError(null);
    setAddSuccess(false);

    try {
      // Prepare create data
      const createData = {
        name: addFormData.name.trim(),
        is_active: addFormData.is_active
      };

      // Include description if provided
      if (addFormData.description && addFormData.description.trim() !== '') {
        createData.description = addFormData.description.trim();
      }
      // Daily objective: hours (0-24) and minutes (0-59), max 24h
      const h = addFormData.daily_objective_hours;
      const m = addFormData.daily_objective_minutes;
      createData.daily_objective_hours = Math.min(24, Math.max(0, parseInt(h, 10) || 7));
      createData.daily_objective_minutes = Math.min(59, Math.max(0, parseInt(m, 10) || 0));
      if (createData.daily_objective_hours === 24) {
        createData.daily_objective_minutes = 0;
      }

      await api.createCentre(createData);
      
      // Refresh centres list
      await fetchCentres();
      
      // Close modal and reset form
      setShowAddCentreModal(false);
      setAddFormData({
        name: '',
        description: '',
        is_active: true,
        daily_objective_hours: 7,
        daily_objective_minutes: 0
      });
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 3000);
    } catch (error) {
      const formattedError = formatApiError(error);
      setAddError(formattedError.generalError || t('centres.createFailed'));
      console.error('Error creating centre:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveCentre = async () => {
    if (!selectedCentre) return;

    // Validate required fields
    if (!editFormData.name || editFormData.name.trim() === '') {
      setSaveError(t('centres.nameRequired'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Prepare update data
      const updateData = {
        name: editFormData.name.trim(),
        is_active: editFormData.is_active
      };

      // Include description if provided
      if (editFormData.description !== undefined) {
        updateData.description = editFormData.description.trim() || null;
      }
      // Daily objective: hours (0-24) and minutes (0-59), max 24h
      const h = editFormData.daily_objective_hours;
      const m = editFormData.daily_objective_minutes;
      if (h != null && h !== '') {
        const hNum = Math.min(24, Math.max(0, parseInt(h, 10) || 0));
        updateData.daily_objective_hours = hNum;
      }
      if (m != null && m !== '') {
        const mNum = Math.min(59, Math.max(0, parseInt(m, 10) || 0));
        updateData.daily_objective_minutes = mNum;
      }
      if (updateData.daily_objective_hours === 24 && (updateData.daily_objective_minutes || 0) > 0) {
        updateData.daily_objective_minutes = 0;
      }

      const response = await api.updateCentre(selectedCentre, updateData);
      
      // Update the centre in the local state with the response data
      setCentres(prevCentres => 
        prevCentres.map(centre => {
          if (centre.id === selectedCentre) {
            const updatedRawData = response || {
              ...centre.rawData,
              ...updateData
            };
            return mapCentreToDisplay(updatedRawData);
          }
          return centre;
        })
      );
      
      // Close the selected centre panel
      setSelectedCentre(null);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      const formattedError = formatApiError(error);
      setSaveError(formattedError.generalError || t('centres.saveFailed'));
      console.error('Error updating centre:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCentreStatus = async (centreId, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = !currentStatus;
    
    // Optimistically update the UI
    setCentres(prevCentres => 
      prevCentres.map(centre => {
        if (centre.id === centreId) {
          const updatedRawData = { ...centre.rawData, is_active: newStatus };
          return {
            ...centre,
            status: newStatus ? t('common:active') : t('common:inactive'),
            statusColor: newStatus ? 'emerald' : 'red',
            rawData: updatedRawData
          };
        }
        return centre;
      })
    );

    try {
      await api.updateCentre(centreId, { is_active: newStatus });
    } catch (error) {
      // Revert on error
      setCentres(prevCentres => 
        prevCentres.map(centre => {
          if (centre.id === centreId) {
            const updatedRawData = { ...centre.rawData, is_active: currentStatus };
            return {
              ...centre,
              status: currentStatus ? t('common:active') : t('common:inactive'),
              statusColor: currentStatus ? 'emerald' : 'red',
              rawData: updatedRawData
            };
          }
          return centre;
        })
      );
      const formattedError = formatApiError(error);
      alert(formattedError.generalError || t('centres.statusUpdateFailed'));
      console.error('Error toggling centre status:', error);
    }
  };

  const handleDeleteClick = async (centreId, centreName, employeeCount, e) => {
    e.stopPropagation();
    setDeleteTargetCentreId('');
    if ((employeeCount || 0) > 0) {
      try {
        const list = await api.getCentresFilter();
        const centresList = Array.isArray(list) ? list : [];
        setOtherCentresForDelete(centresList.filter((c) => c.id !== centreId));
      } catch (err) {
        console.error('Error fetching centres for delete dropdown:', err);
        setOtherCentresForDelete([]);
      }
    } else {
      setOtherCentresForDelete([]);
    }
    setDeleteModal({ isOpen: true, centreId, centreName, employeeCount: employeeCount || 0 });
  };

  const handleDeleteConfirm = async () => {
    const { centreId, centreName, employeeCount } = deleteModal;
    if (!centreId) return;

    if (employeeCount > 0) {
      if (!deleteTargetCentreId || deleteTargetCentreId === '') {
        setDeleteError(t('centres.selectReassignTarget'));
        return;
      }
      setDeleteError(null);
    }

    // Optimistically remove from UI
    const centreToDelete = centres.find(c => c.id === centreId);
    setCentres(prevCentres => prevCentres.filter(centre => centre.id !== centreId));
    setCentresCount(prevCount => Math.max(0, prevCount - 1));

    if (selectedCentre === centreId) {
      setSelectedCentre(null);
    }

    setDeleteModal({ isOpen: false, centreId: null, centreName: null, employeeCount: 0 });
    setDeleteTargetCentreId('');

    try {
      const options = employeeCount > 0 && deleteTargetCentreId
        ? { target_centre_id: parseInt(deleteTargetCentreId, 10) }
        : {};
      await api.deleteCentre(centreId, options);
      await fetchCentres();
      showSuccess(
        t('centres.deletedTitle'),
        employeeCount > 0
          ? t('centres.deletedWithReassign', { name: centreName, count: employeeCount })
          : t('centres.deletedMessage', { name: centreName })
      );
    } catch (error) {
      if (centreToDelete) {
        setCentres(prevCentres => [...prevCentres, centreToDelete].sort((a, b) => a.id - b.id));
        setCentresCount(prevCount => prevCount + 1);
      }
      const formattedError = formatApiError(error);
      showError(t('centres.deleteFailed'), formattedError.generalError || t('centres.deleteFailedMessage'));
      if (error.response?.status === 403) {
        showWarning(t('centres.permissionDenied'), t('centres.permissionDeniedMessage'), 5000);
      }
      console.error('Error deleting centre:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, centreId: null, centreName: null, employeeCount: 0 });
    setDeleteTargetCentreId('');
    setDeleteError(null);
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, centresCount);
  const totalPages = Math.ceil(centresCount / pageSize);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getStatusBadgeClass = (statusColor) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70',
      red: 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70'
    };
    return colors[statusColor] || colors.emerald;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedCentres = () => {
    if (!sortColumn) return centres;

    return [...centres].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'employeeCount':
          aValue = a.employeeCount || 0;
          bValue = b.employeeCount || 0;
          break;
        case 'createdDate':
          aValue = a.rawData?.created_at ? new Date(a.rawData.created_at).getTime() : 0;
          bValue = b.rawData?.created_at ? new Date(b.rawData.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ column, children }) => {
    const isActive = sortColumn === column;
    return (
      <th 
        className="px-6 py-3 font-semibold cursor-pointer hover:bg-slate-100 transition-colors select-none"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <div className="flex flex-col">
            <span className={`material-symbols-outlined text-[14px] leading-none ${
              isActive && sortDirection === 'asc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_up
            </span>
            <span className={`material-symbols-outlined text-[14px] leading-none -mt-1 ${
              isActive && sortDirection === 'desc' ? 'text-primary' : 'text-slate-300'
            }`}>
              arrow_drop_down
            </span>
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Heading & Actions */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('centres.title')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('centres.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleOpenAddModal}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span className="truncate">{t('centres.addNewCentre')}</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col min-w-64 flex-1">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider pb-2">
              {t('centres.searchLabel')}
            </p>
            <div className="relative">
              <input
                className="form-input w-full rounded-lg text-slate-900 dark:text-slate-100 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 h-10 placeholder:text-slate-400 dark:placeholder-slate-400 pl-10 pr-4 text-sm"
                placeholder={t('centres.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
            </div>
          </label>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('common:status')}</p>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 appearance-none rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 pr-10 min-w-[140px] text-slate-700 dark:text-slate-100 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('common:all')}</option>
                  <option value="active">{t('common:active')}</option>
                  <option value="inactive">{t('common:inactive')}</option>
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors ml-2"
            >
              {t('common:clearAllFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* Centres Table */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">{t('centres.title')}</h3>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {t('centres.count', { count: centresCount })}
              </span>
            </div>
            <div className="overflow-x-auto">
            {centresError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{centresError}</div>
              </div>
            ) : centres.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('centres.noCentres')}</div>
              </div>
            ) : (
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <tr>
                    <SortableHeader column="name">{t('centres.centre')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold">{t('common:description')}</th>
                    <SortableHeader column="employeeCount">{t('centres.employees')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold">{t('common:status')}</th>
                    <SortableHeader column="createdDate">{t('centres.createdAt')}</SortableHeader>
                    <th className="px-6 py-3 font-semibold text-right">{t('common:actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {getSortedCentres().map((centre) => (
                  <motion.tr
                    key={centre.id}
                    className={`hover:bg-slate-50 transition-colors group cursor-pointer border-l-4 ${
                      selectedCentre === centre.id
                        ? 'bg-primary/5'
                        : 'border-l-transparent'
                    }`}
                    onClick={() => {
                      setSelectedCentre(selectedCentre === centre.id ? null : centre.id);
                    }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <span className="material-symbols-outlined text-[20px]">location_city</span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{centre.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 text-sm max-w-xs truncate" title={centre.description}>
                        {centre.description || t('centres.noDescription')}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80">
                        {centre.employeeCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(centre.statusColor)}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          centre.statusColor === 'emerald' ? 'bg-emerald-500' : 'bg-red-500'
                        }`}></span>
                        {centre.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{centre.createdDate}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2.5">
                        {/* Toggle Active/Inactive Button */}
                        <button
                          onClick={(e) => handleToggleCentreStatus(centre.id, centre.rawData?.is_active, e)}
                          className={`h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm ${
                            centre.rawData?.is_active
                              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-700 border border-emerald-300/50 dark:from-emerald-900/40 dark:to-emerald-800/30 dark:text-emerald-200 dark:border-emerald-700/70'
                              : 'bg-gradient-to-r from-slate-100 to-slate-50 text-slate-600 border border-slate-300/50 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300 dark:border-slate-600'
                          }`}
                          title={centre.rawData?.is_active ? t('centres.deactivate') : t('centres.activate')}
                        >
                          <span 
                            className={`material-symbols-outlined text-[18px] ${
                              centre.rawData?.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
                            }`}
                          >
                            {centre.rawData?.is_active ? 'check_circle' : 'cancel'}
                          </span>
                          <span className="font-medium">{centre.rawData?.is_active ? t('common:active') : t('common:inactive')}</span>
                        </button>
                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCentre(selectedCentre === centre.id ? null : centre.id);
                          }}
                          className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70 dark:hover:bg-blue-900/60 transition-colors"
                          title={t('centres.editCentre')}
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            edit
                          </span>
                          <span className="font-medium">{t('common:edit')}</span>
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDeleteClick(centre.id, centre.name, centre.employeeCount, e)}
                          className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm bg-gradient-to-r from-red-50 to-red-100/50 text-red-600 border border-red-300/50 dark:from-red-900/30 dark:to-red-800/20 dark:text-red-200 dark:border-red-700/70"
                          title={t('centres.deleteCentre')}
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            delete_outline
                          </span>
                          <span className="font-medium">{t('common:delete')}</span>
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {t('common:showingRange', { from: centresCount > 0 ? startIndex : 0, to: endIndex, total: centresCount })}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('common:perPage')}</span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="appearance-none rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 pr-8 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:firstPage')}
                >
                  <span className="material-symbols-outlined text-sm">first_page</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:previousPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:nextPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:lastPage')}
                >
                  <span className="material-symbols-outlined text-sm">last_page</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Centre Edit Modal */}
      <AnimatePresence>
        {selectedCentre && selectedCentreData && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setSelectedCentre(null)}
            />
            {/* Modal */}
            <div onClick={() => setSelectedCentre(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedCentre(null);
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('centres.selectedCentre')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{selectedCentreData.name}</h3>
                    <p className="text-sm text-slate-500">
                      {t('centres.employeesAssigned', { count: selectedCentreData.employeeCount || 0 })}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedCentre(null)}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('common:closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {saveSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('centres.updated')}
                </div>
              )}
              {saveError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {saveError}
                </div>
              )}

              {/* Centre Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('centres.name')} *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('centres.namePlaceholder')}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:description')}</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('centres.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">{t('centres.dailyObjective')}</label>
                  <div className="flex flex-col gap-3">
                    <DailyObjectiveSpinner
                      hours={editFormData.daily_objective_hours}
                      minutes={editFormData.daily_objective_minutes}
                      onChange={(newHours, newMinutes) => {
                        handleFormChange('daily_objective_hours', newHours);
                        handleFormChange('daily_objective_minutes', newMinutes);
                        setSaveError(null);
                      }}
                      hasError={!!(saveError && saveError.toLowerCase().includes('daily objective'))}
                      ariaErrorId="edit-daily-obj-error"
                      ariaHintId="edit-daily-obj-hint"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">{t('centres.quickPresets')}</span>
                      {DAILY_OBJECTIVE_PRESETS.map((preset) => (
                        <button
                          key={preset.labelKey}
                          type="button"
                          onClick={() => {
                            handleFormChange('daily_objective_hours', preset.hours);
                            handleFormChange('daily_objective_minutes', preset.minutes);
                            setSaveError(null);
                          }}
                          className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          {t(preset.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p id="edit-daily-obj-hint" className="text-xs text-slate-500">
                    {t('centres.dailyObjectiveHint')}
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:status')}</label>
                  <div ref={statusContainerRef} className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    {/* Animated sliding background */}
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: editFormData.is_active ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active"
                        checked={editFormData.is_active === true}
                        onChange={() => handleFormChange('is_active', true)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={activeStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.is_active ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:active')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active"
                        checked={editFormData.is_active === false}
                        onChange={() => handleFormChange('is_active', false)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={inactiveStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.is_active === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:inactive')}
                      </motion.div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={handleSaveCentre}
                  disabled={saving}
                  className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('common:saving') : t('centres.saveChanges')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Add Centre Modal */}
      <AnimatePresence>
        {showAddCentreModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleCloseAddModal}
            />
            {/* Modal */}
            <div onClick={handleCloseAddModal} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    handleCloseAddModal();
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('centres.createNewEyebrow')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{t('centres.addCentre')}</h3>
                    <p className="text-sm text-slate-500">
                      {t('centres.createSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseAddModal}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('common:closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {addSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('centres.created')}
                </div>
              )}
              {addError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {addError}
                </div>
              )}

              {/* Centre Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('centres.name')} *</label>
                  <input
                    type="text"
                    value={addFormData.name}
                    onChange={(e) => handleAddFormChange('name', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('centres.namePlaceholder')}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:description')}</label>
                  <textarea
                    value={addFormData.description}
                    onChange={(e) => handleAddFormChange('description', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none placeholder-slate-500 dark:placeholder-slate-400"
                    placeholder={t('centres.descriptionPlaceholder')}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">{t('centres.dailyObjective')}</label>
                  <div className="flex flex-col gap-3">
                    <DailyObjectiveSpinner
                      hours={addFormData.daily_objective_hours}
                      minutes={addFormData.daily_objective_minutes}
                      onChange={(newHours, newMinutes) => {
                        handleAddFormChange('daily_objective_hours', newHours);
                        handleAddFormChange('daily_objective_minutes', newMinutes);
                        setAddError(null);
                      }}
                      hasError={!!(addError && addError.toLowerCase().includes('daily objective'))}
                      ariaErrorId="add-daily-obj-error"
                      ariaHintId="add-daily-obj-hint"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">{t('centres.quickPresets')}</span>
                      {DAILY_OBJECTIVE_PRESETS.map((preset) => (
                        <button
                          key={preset.labelKey}
                          type="button"
                          onClick={() => {
                            handleAddFormChange('daily_objective_hours', preset.hours);
                            handleAddFormChange('daily_objective_minutes', preset.minutes);
                            setAddError(null);
                          }}
                          className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          {t(preset.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p id="add-daily-obj-hint" className="text-xs text-slate-500">
                    {t('centres.dailyObjectiveHint')}
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:status')}</label>
                  <div ref={statusContainerRef} className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    {/* Animated sliding background */}
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: addFormData.is_active ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === true}
                        onChange={() => handleAddFormChange('is_active', true)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={activeStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:active')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === false}
                        onChange={() => handleAddFormChange('is_active', false)}
                        className="sr-only"
                      />
                      <motion.div
                        ref={inactiveStatusRef}
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:inactive')}
                      </motion.div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Create Button */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button
                  onClick={handleAddCentre}
                  disabled={adding}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[18px]">add</span>
                  {t('centres.createCentre')}
                </button>
                <button
                  onClick={handleCloseAddModal}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  {t('common:cancel')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={handleDeleteCancel}
            />
            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="material-symbols-outlined text-red-600 text-[24px]">
                        warning
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-2">
                        {t('centres.deleteCentre')}
                      </h3>
                      {deleteModal.employeeCount > 0 ? (
                        <>
                          <p className="text-sm text-slate-600 mb-2">
                            {t('centres.deleteHasEmployees', { count: deleteModal.employeeCount })}
                          </p>
                          {deleteError && (
                            <p className="text-sm text-red-600 font-medium mb-2">{deleteError}</p>
                          )}
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            {t('centres.reassignEmployeesTo')}
                          </label>
                          <select
                            value={deleteTargetCentreId}
                            onChange={(e) => { setDeleteTargetCentreId(e.target.value); setDeleteError(null); }}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 p-2.5 pr-10 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none cursor-pointer"
                          >
                            <option value="">{t('centres.selectCentre')}</option>
                            {otherCentresForDelete.map((c) => (
                              <option key={c.id} value={c.id.toString()}>{c.name}</option>
                            ))}
                          </select>
                          {otherCentresForDelete.length === 0 && (
                            <p className="text-sm text-amber-600 mt-1">{t('centres.noOtherCentre')}</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-slate-600 mb-1">
                            {t('centres.confirmDeleteNamed', { name: deleteModal.centreName })}
                          </p>
                          <p className="text-sm text-red-600 font-medium">
                            {t('common:actionCannotBeUndone')}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-3 mt-6">
                    <button
                      onClick={handleDeleteCancel}
                      className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      {t('common:cancel')}
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={deleteModal.employeeCount > 0 && (!deleteTargetCentreId || deleteTargetCentreId === '')}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleteModal.employeeCount > 0 ? t('centres.reassignAndDelete') : t('common:delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />
    </div>
  );
};

export default CentresPage;

